import type {
  DataSourceTableViewLayoutState,
  DataSourceViewLayoutState,
  DataSourceViewWindowState,
} from '@blocksuite/data-view';
import { signal } from '@preact/signals-core';

import type {
  DatabaseLocalUiState,
  DatabaseUiStatePersistence,
} from './data-source-contracts.js';
import {
  DATABASE_TABLE_LAYOUT_STATE_VERSION,
  DATABASE_VIEW_LAYOUT_STATE_VERSION,
  omitDatabaseLocalUiStateView,
  readDatabaseLocalUiState,
  writeDatabaseLocalUiState,
} from './data-source-ui-state.js';
import type { DatabaseBlockViewManager } from './data-source-view-manager.js';

interface DatabaseLocalUiHost {
  rootDocId(): string;
  databaseId(): string;
  viewManager(): DatabaseBlockViewManager;
  rowIdsForLayout(): Set<string> | undefined;
}

export class DatabaseLocalUiController {
  constructor(private readonly host: DatabaseLocalUiHost) {}

  private _uiStatePersistence: DatabaseUiStatePersistence = 'durable';

  private readonly _localViewWindowStates$ = signal<
    Record<string, DataSourceViewWindowState>
  >({});

  setUiStatePersistence(mode: DatabaseUiStatePersistence): void {
    if (this._uiStatePersistence === mode) {
      return;
    }
    this._uiStatePersistence = mode;
    const localState =
      mode === 'local' ? this.readLocalUiState() : ({} as DatabaseLocalUiState);
    this._localViewWindowStates$.value = localState.windows ?? {};
    this.host.viewManager().setCurrentViewPersistence(mode, localState.viewId);
  }

  persistLocalCurrentView(viewId: string | undefined): void {
    if (this._uiStatePersistence !== 'local') {
      return;
    }
    this.writeLocalUiState({
      ...this.readLocalUiState(),
      viewId,
    });
  }

  setTransientCurrentView(id: string | undefined): void {
    this.host.viewManager().setTransientCurrentView(id);
  }

  getViewWindowState(viewId: string): DataSourceViewWindowState | undefined {
    if (
      this._uiStatePersistence !== 'local' &&
      this._uiStatePersistence !== 'transient'
    ) {
      return undefined;
    }
    return this._localViewWindowStates$.value[viewId];
  }

  setViewWindowState(
    viewId: string,
    patch: DataSourceViewWindowState
  ): boolean {
    if (
      this._uiStatePersistence !== 'local' &&
      this._uiStatePersistence !== 'transient'
    ) {
      return false;
    }
    const nextWindow = {
      ...(this._localViewWindowStates$.value[viewId] ?? {}),
      ...patch,
    };
    const next = {
      ...this._localViewWindowStates$.value,
      [viewId]: nextWindow,
    };
    this._localViewWindowStates$.value = next;
    if (this._uiStatePersistence === 'local') {
      const stored = this.readLocalUiState();
      this.writeLocalUiState({
        ...stored,
        windows: {
          ...(stored.windows ?? {}),
          [viewId]: nextWindow,
        },
      });
    }
    return true;
  }

  getViewLayoutState(
    viewId: string,
    viewMode: string
  ): DataSourceViewLayoutState | undefined {
    // Layout metrics are local render cache, not document/user preference.
    // They must be available on every surface, including page-mode databases
    // whose persisted view/window state intentionally remains durable.
    const state = this.readLocalUiState().viewLayouts?.[viewId];
    if (!state || state.viewMode !== viewMode) {
      return undefined;
    }
    return state;
  }

  setViewLayoutState(
    viewId: string,
    state: DataSourceViewLayoutState
  ): boolean {
    if (
      state.version !== DATABASE_VIEW_LAYOUT_STATE_VERSION ||
      typeof state.intrinsicContentHeight !== 'number' ||
      !Number.isFinite(state.intrinsicContentHeight) ||
      state.intrinsicContentHeight <= 0
    ) {
      return false;
    }
    const stored = this.readLocalUiState();
    this.writeLocalUiState({
      ...stored,
      viewLayouts: {
        ...(stored.viewLayouts ?? {}),
        [viewId]: {
          ...state,
          version: DATABASE_VIEW_LAYOUT_STATE_VERSION,
        },
      },
    });
    return true;
  }

  getTableViewLayoutState(
    viewId: string,
    columnLayoutSignature: string
  ): DataSourceTableViewLayoutState | undefined {
    // Row heights are local render cache and must hydrate before the first
    // virtual table pass on page, fullscreen, and edgeless surfaces alike.
    const state = this.readLocalUiState().tableLayouts?.[viewId];
    if (state?.columnLayoutSignature !== columnLayoutSignature) {
      return undefined;
    }
    const rowIds = this.host.rowIdsForLayout();
    // On first page load the database root can mount before row shards have
    // populated the logical row snapshot. An empty snapshot means "unknown
    // yet", not "this database has no rows"; keep cached row heights so the
    // first virtual-scroller pass can hydrate without later row jumps.
    const rowHeights = Object.fromEntries(
      Object.entries(state.rowHeights).filter(
        ([rowId]) => !rowIds || rowIds.size === 0 || rowIds.has(rowId)
      )
    );
    if (Object.keys(rowHeights).length === 0 && !state.intrinsicContentHeight) {
      return undefined;
    }
    return {
      ...state,
      rowHeights,
    };
  }

  setTableViewLayoutState(
    viewId: string,
    state: DataSourceTableViewLayoutState
  ): boolean {
    const rowIds = this.host.rowIdsForLayout();
    const rowHeights = Object.fromEntries(
      Object.entries(state.rowHeights).filter(([rowId, height]) => {
        return (
          (!rowIds || rowIds.size === 0 || rowIds.has(rowId)) &&
          typeof height === 'number' &&
          Number.isFinite(height) &&
          height > 0
        );
      })
    );
    const stored = this.readLocalUiState();
    this.writeLocalUiState({
      ...stored,
      tableLayouts: {
        ...(stored.tableLayouts ?? {}),
        [viewId]: {
          ...state,
          rowHeights,
          version: DATABASE_TABLE_LAYOUT_STATE_VERSION,
        },
      },
    });
    return true;
  }

  private readLocalUiState(): DatabaseLocalUiState {
    return readDatabaseLocalUiState(
      this.host.rootDocId(),
      this.host.databaseId()
    );
  }

  private writeLocalUiState(state: DatabaseLocalUiState): void {
    writeDatabaseLocalUiState(
      this.host.rootDocId(),
      this.host.databaseId(),
      state
    );
  }

  pruneLocalUiStateForDeletedView(
    viewId: string,
    nextViewId: string | undefined
  ): void {
    const stored = this.readLocalUiState();
    const nextWindows = omitDatabaseLocalUiStateView(stored.windows, viewId);

    this._localViewWindowStates$.value =
      omitDatabaseLocalUiStateView(
        this._localViewWindowStates$.value,
        viewId
      ) ?? {};

    this.writeLocalUiState({
      ...stored,
      tableLayouts: omitDatabaseLocalUiStateView(stored.tableLayouts, viewId),
      viewLayouts: omitDatabaseLocalUiStateView(stored.viewLayouts, viewId),
      viewId: stored.viewId === viewId ? nextViewId : stored.viewId,
      windows: nextWindows,
    });
  }
}
